import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom"; // Assuming you get journalId from the URL
import axios from "axios";
import PageLayout from "src/pages/PageLayout";
import { STATIC } from "src/network/networkConfig";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";
import {
  getJournalDetailsById,
  journalPublishedPapersApi,
} from "src/network/api";
import { useNavigate } from "react-router-dom";

const JournalProfile = () => {
  type Journal = {
    image: string;
    first_volume: string;
    number_of_issue_per_year: string;
    country_name: string;
    state_name: string;
    publisher_name: string;
    journal_submission_link: any;
    indexing: string;
    publication_type: string;
    usd_publication_charge: string;
    review_type: string;
    review_time: string;
    approval_status: string;
    created_at: string;
    eissn_no: string;
    pissn_no: string;
    journal_name: string;
    description: string;
    broad_research_area: string;
    website_link: string | URL;
    impact_factor: string;
    language: string;
    doi: string;
  };
  const { journal_id } = useParams(); // Get journalId from URL params
  const [journals, setJournals] = useState<Journal>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showMoreTags, setShowMoreTags] = useState(false);
  const [publishedPapers, setPublishedPapers] = useState([]);
  const [type, setType] = useState("");
  const [charge, setCharge] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchJournalDetails = async () => {
      try {
        const response = await getJournalDetailsById(journal_id);
        setType(response.data.data.publication_type);
        setCharge(response.data.data.usd_publication_charge);
        setJournals(response.data.data);
      } catch (err) {
        setError("Failed to load journal details");
      } finally {
        setLoading(false);
      }
    };

    const fetchPublishedPapers = async () => {
      try {
        const response = await journalPublishedPapersApi(journal_id);
        if (response.data.status === 200) {
          setPublishedPapers(response.data.data);
        }
      } catch (err) {
        console.error("Error fetching published papers", err);
      }
    };

    fetchJournalDetails();
    fetchPublishedPapers();
  }, [journal_id]);

  return (
    <PageLayout>
      <div
        className="container-fluid py-6 px-3 px-md-5"
        style={{ backgroundColor: "#f8f9fa" }}
      >
        <div className="row">
          {/* Left Column */}
          <div className="col-lg-12 mb-4">
            {/* Profile Card */}
            <div
              className="card shadow-lg mb-4"
              style={{ backgroundColor: "#ffffff", borderRadius: "5px" }}
            >
              <div className="card-body position-relative">
                {journals?.website_link && (
                  <a
                    className="btn btn-primary position-absolute"
                    style={{ top: "10px", right: "10px" }}
                    href={`${journals.website_link}`}
                    target="_blank"
                  >
                    Visit Website <i className="fa fa-external-link"></i>
                  </a>
                )}

                <div className="d-flex flex-column flex-md-row align-items-center mb-4">
                  <img
                    src={journals?.image || STATIC.noImageURL}
                    alt="Journal"
                    className="mb-3 mb-md-0"
                    style={{
                      width: "100px",
                      height: "100px",
                      objectFit: "cover",
                    }}
                  />

                  <div className="ml-md-4 text-center text-md-left">
                    <h3 className="text-primary font-weight-bold mb-3">
                      {journals?.journal_name}
                    </h3>
                    <p className="badge badge-primary mr-2">
                      <strong>e-ISSN:</strong> {journals?.eissn_no || "N/A"}
                    </p>
                    <p className="badge badge-primary mr-2">
                      <strong>p-ISSN:</strong> {journals?.pissn_no || "N/A"}
                    </p>
                    <a
                      className="badge badge-info ml-2"
                      href={`/journal-certificate/${journal_id}`}
                      target="_blank"
                    >
                      <strong>Certificate</strong>{" "}
                      <i className="fa fa-external-link"></i>
                    </a>
                  </div>
                </div>

                <h5 className="mb-3"></h5>
                <div className="d-flex flex-wrap gap-2">
                  {journals?.broad_research_area ? (
                    journals.broad_research_area.split(",").map((area, idx) => (
                      <button
                        key={idx}
                        className="btn btn-outline-primary"
                        style={{
                          borderRadius: "50px",
                          borderWidth: "1px",
                          fontWeight: "normal",
                          margin: "5px",
                          padding: "5px 10px",
                        }}
                      >
                        {area.trim()}
                      </button>
                    ))
                  ) : (
                    <p className="text-muted">
                      No broad research areas available
                    </p>
                  )}
                </div>

                <hr style={{ borderColor: "gray", opacity: 0.5 }} />

                {/* Columns Layout */}
                <div className="row">
                  {/* Left Column */}
                  <div className="col-4">
                    <p>
                      <strong>First Volume:</strong>{" "}
                      {journals?.first_volume || "N/A"}
                    </p>
                    <p>
                      <strong>Issue Frequency:</strong>{" "}
                      {journals?.number_of_issue_per_year || "N/A"}
                    </p>
                    <p>
                      <strong>Country:</strong>{" "}
                      {journals?.country_name || "N/A"}
                    </p>
                    <p>
                      <strong>DOI:</strong> {journals?.doi || "N/A"}
                    </p>
                  </div>

                  {/* Middle Column */}

                  {/* Right Column */}
                  <div className="col-4">
                    <p>
                      <strong>Publisher Name:</strong>{" "}
                      {journals?.publisher_name || "N/A"}
                    </p>
                    <p>
                      <strong>Publication Type:</strong>{" "}
                      {type == "Free" ? "Free" : "Paid (Rs " + charge + ")"}
                    </p>
                    <p>
                      <strong>Review Type:</strong>{" "}
                      {journals?.review_type || "N/A"}
                    </p>
                    <p>
                      <strong>Impact Factor:</strong>{" "}
                      {journals?.impact_factor || "N/A"}
                    </p>
                  </div>
                  <div className="col-4">
                    <p>
                      <strong>Language:</strong> {journals?.language || "N/A"}
                    </p>
                    <p>
                      <strong>State:</strong> {journals?.state_name || "N/A"}
                    </p>
                    <p>
                      <strong>Review Time:</strong>{" "}
                      {journals?.review_time || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="card shadow-lg mb-4"
              style={{ backgroundColor: "#ffffff", borderRadius: "7px" }}
            >
              <div className="card-body">
                <h4 className="font-weight-bold mb-3">Journal Descriptions</h4>
                <ReactQuill
                  value={`<p>${journals?.description || "N/A"}</p>`}
                  readOnly={true}
                  theme="bubble"
                />
              </div>
            </div>

            {/* index card */}

            <div
              className="card shadow-lg mb-4"
              style={{ backgroundColor: "#ffffff", borderRadius: "5px" }}
            >
              <div className="card-body">
                <h5 className="font-weight-bold mb-3">Indexing</h5>
                <div className="row">
                  {journals?.indexing ? (
                    journals.indexing.split(",").map((index, idx) => (
                      <div key={idx} className="col-4">
                        <div className="d-flex align-items-center mb-3">
                          <div
                            className="bg-primary"
                            style={{
                              width: "20px",
                              height: "20px",
                              borderRadius: "4px",
                              marginRight: "10px",
                            }}
                          ></div>
                          <h6 className="font-weight-bold mb-0">
                            {index.trim()}
                          </h6>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted">No indexing available</p>
                  )}
                </div>
              </div>
            </div>

            {/* Publication Card */}
            <div
              className="card shadow-lg mb-4"
              style={{ backgroundColor: "#ffffff" }}
            >
              <div className="card-body">
                <h5 className="font-weight-bold mb-4">
                  Publications of {journals?.journal_name}
                </h5>
                {publishedPapers.length > 0 ? (
                  publishedPapers.map((paper, index) => (
                    <React.Fragment key={index}>
                      <div className="d-flex mb-4">
                        <div>
                          <h5>
                            <a
                              href={
                                paper.live_url.startsWith("http")
                                  ? paper.live_url
                                  : `https://${paper.live_url}`
                              }
                              className="custom-link"
                              target="_blank"
                            >
                              {paper.paper_title}
                            </a>
                          </h5>

                          <p className="text-muted mb-1">
                            {/* {} */}
                            <ExpandableBox data={paper.abstract} />
                          </p>
                        </div>
                      </div>
                      {index < publishedPapers.length - 1 && (
                        <hr style={{ borderColor: "gray", opacity: 0.5 }} />
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <p className="text-muted">No published papers available.</p>
                )}
              </div>
            </div>

            {/* Right Column */}
            {/* <div className="col-12 col-lg-4">
            <div
              className="card shadow-lg mb-5"
              style={{ backgroundColor: "#ffffff", borderRadius: "7px" }}
            >
              <div className="card-body text-center">
                <h5 className="card-title">ENTOMOLOGICAL NEWS</h5>
                <p className="badge badge-danger mr-2">ISSN-No: 0013-872X</p>
                <p className="badge badge-danger">PISSN-No: 0013-872X</p>
                <button className="btn btn-outline-primary btn-sm mb-2">
                  Get More Details (Price/Time)
                </button>
                <p>Certificate Views: 48608</p>
                <p>Publication: Free</p>
                <button className="btn btn-primary btn-sm">
                  Click to Get More Help
                </button>
              </div>
            </div>
          </div> */}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

const ExpandableBox = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = React.useRef(null);

  useEffect(() => {
    if (contentRef.current) {
      setIsOverflowing(contentRef.current.scrollHeight > 55);
    }
  }, [data]);
  if (data?.trim().length == 0) return null;
  return (
    <div>
      <div
        ref={contentRef}
        style={{
          maxHeight: expanded ? "none" : "55px",
          overflow: "hidden",
          transition: "max-height 0.3s ease",
          textAlign: "justify",
        }}
      >
        {data}
      </div>
      {isOverflowing && (
        <p style={{ textAlign: "right" }}>
          <a
            onClick={() => setExpanded(!expanded)}
            style={{ cursor: "pointer" }}
          >
            Read {expanded ? "Less" : "More"}
          </a>
        </p>
      )}
    </div>
  );
};

export default JournalProfile;
